import React, { useState, useEffect } from 'react';
import { Users, UserCheck, IndianRupee, Plus, Download, Search, Trash2, Pencil, MoreVertical, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import HardDeletePartnerDialog from '../../components/HardDeletePartnerDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState({ partners: 0, clients: 0, revenue: 0 });
  const [partners, setPartners] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', mobile: '' });
  const [restorePrompt, setRestorePrompt] = useState(null); // { id, name, email, deleted_at, client_count }
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null);
  const [hardDeleteConfirmText, setHardDeleteConfirmText] = useState('');
  const [hardDeleting, setHardDeleting] = useState(false);
  const [hardDeleteResult, setHardDeleteResult] = useState(null);

  // Auth header helper — admin endpoints now require Bearer token (post C-1 audit fix)
  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
  });

  // Fetch dashboard stats
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, { headers: authHeaders() });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  // Fetch partners
  const fetchPartners = async (search = '') => {
    try {
      const params = search ? { search } : {};
      const response = await axios.get(`${API}/partners`, { params, headers: authHeaders() });
      setPartners(response.data.partners);
    } catch (error) {
      console.error('Failed to fetch partners:', error);
      toast.error('Failed to load partners');
    }
  };

  useEffect(() => {
    fetchStats();
    fetchPartners();
  }, []);

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    fetchPartners(value);
  };

  // Handle form change
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Open Add Modal
  const openAddModal = () => {
    setEditingPartner(null);
    setFormData({ name: '', email: '', mobile: '' });
    setIsModalOpen(true);
  };

  // Open Edit Modal
  const openEditModal = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      email: partner.email,
      mobile: partner.mobile,
    });
    setIsModalOpen(true);
  };

  // Close Modal
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingPartner(null);
    setFormData({ name: '', email: '', mobile: '' });
  };

  // Add or Update partner
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || formData.name.trim().length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }

    if (!/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(formData.email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!formData.mobile || !/^\d{10}$/.test(formData.mobile.trim().replace(/[\s\-\+]/g, '').slice(-10))) {
      toast.error('Mobile number must be 10 digits');
      return;
    }

    setIsLoading(true);

    try {
      if (editingPartner) {
        // Update existing partner
        await axios.put(`${API}/partners/${editingPartner.id}`, formData, { headers: authHeaders() });
        toast.success('Partner updated successfully!');
      } else {
        // Add new partner
        await axios.post(`${API}/partners`, formData, { headers: authHeaders() });
        toast.success('Partner added successfully!');
      }
      closeModal();
      fetchPartners(searchQuery);
      fetchStats();
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Handle 409 restore flow: backend returns { message, can_restore, deleted_partner }
      if (error.response?.status === 409 && detail && typeof detail === 'object' && detail.can_restore) {
        setRestorePrompt(detail.deleted_partner);
        setIsLoading(false);
        return;
      }
      const message = Array.isArray(detail) ? detail.map(e => e.msg?.replace('Value error, ', '')).join(', ') : (typeof detail === 'string' ? detail : 'Operation failed');
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Restore a soft-deleted partner (from the 409 prompt)
  const handleRestoreDeleted = async () => {
    if (!restorePrompt?.id) return;
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/partners/${restorePrompt.id}/restore`, null, { headers: authHeaders() });
      toast.success(res.data?.message || 'Partner restored successfully');
      setRestorePrompt(null);
      closeModal();
      fetchPartners(searchQuery);
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore partner');
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle partner status
  const handleToggleStatus = async (partnerId, currentStatus) => {
    try {
      await axios.patch(`${API}/partners/${partnerId}/toggle-status`, null, { headers: authHeaders() });
      const newStatus = currentStatus === 'Active' ? 'Disabled' : 'Active';
      toast.success(`Partner ${newStatus === 'Active' ? 'enabled' : 'disabled'} successfully`);
      fetchPartners(searchQuery);
    } catch (error) {
      toast.error('Failed to update partner status');
    }
  };

  // Delete partner (soft)
  const handleDeletePartner = async (partnerId) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) return;

    try {
      await axios.delete(`${API}/partners/${partnerId}`, { headers: authHeaders() });
      toast.success('Partner deleted successfully');
      fetchPartners(searchQuery);
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete partner');
    }
  };

  // Open hard-delete dialog
  const openHardDelete = (partner) => {
    setHardDeleteTarget(partner);
    setHardDeleteConfirmText('');
    setHardDeleteResult(null);
  };

  // Execute hard-delete
  const handleHardDelete = async () => {
    if (!hardDeleteTarget) return;
    const expected = (hardDeleteTarget.email || '').trim().toLowerCase();
    if (hardDeleteConfirmText.trim().toLowerCase() !== expected) {
      toast.error(`Type "${hardDeleteTarget.email}" exactly to confirm`);
      return;
    }
    setHardDeleting(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${API}/partners/${hardDeleteTarget.id}/hard-delete`,
        { confirm_email: hardDeleteConfirmText.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHardDeleteResult(res.data);
      toast.success('Partner and all related data permanently deleted');
      fetchPartners(searchQuery);
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to hard-delete partner');
    } finally {
      setHardDeleting(false);
    }
  };

  const closeHardDelete = () => {
    setHardDeleteTarget(null);
    setHardDeleteConfirmText('');
    setHardDeleteResult(null);
  };

  // Download partners CSV
  const handleDownload = () => {
    window.open(`${API}/partners/download/csv`, '_blank');
  };

  const statCards = [
    {
      label: 'Total Partners',
      value: stats.partners,
      icon: Users,
      color: 'amber',
    },
    {
      label: 'Total Clients',
      value: stats.clients,
      icon: UserCheck,
      color: 'emerald',
    },
    {
      label: 'Amount Generated',
      value: stats.revenue > 0 ? `₹${stats.revenue.toLocaleString()}` : '—',
      icon: IndianRupee,
      color: 'blue',
    },
  ];

  const colorClasses = {
    amber: { bg: 'bg-amber-500/10', text: 'text-amber-400' },
    emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-400' },
    blue: { bg: 'bg-blue-500/10', text: 'text-blue-400' },
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Dashboard</h1>
          <p className="text-slate-400 mt-1">Welcome back! Here's what's happening today.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={handleDownload}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white"
          >
            <Download className="w-4 h-4 mr-2" />
            Download Partners
          </Button>
          <Button
            onClick={openAddModal}
            className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Partner
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          const colors = colorClasses[stat.color];
          return (
            <Card key={index} className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-slate-400 text-sm font-medium">{stat.label}</p>
                    <p className="text-3xl font-bold text-white mt-2">
                      {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                    </p>
                  </div>
                  <div className={`p-3 rounded-xl ${colors.bg}`}>
                    <Icon className={`w-6 h-6 ${colors.text}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Partners Table */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-white">Partners</CardTitle>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                type="text"
                placeholder="Search by name..."
                value={searchQuery}
                onChange={handleSearch}
                className="pl-9 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {partners.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Users className="w-12 h-12 text-slate-600 mb-4" />
              <p className="text-slate-400 text-lg">No partners yet</p>
              <p className="text-slate-500 text-sm mt-1">Click "Add Partner" to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="text-left p-4 text-slate-400 font-medium">Name</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Email</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Mobile</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Added On</th>
                    <th className="text-right p-4 text-slate-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {partners.map((partner) => (
                    <tr
                      key={partner.id}
                      className={`border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors ${
                        partner.status === 'Disabled' ? 'opacity-60' : ''
                      }`}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            partner.status === 'Active' ? 'bg-slate-800' : partner.status === 'Pending' ? 'bg-amber-500/20' : 'bg-slate-800/50'
                          }`}>
                            <span className={`font-semibold ${
                              partner.status === 'Active' ? 'text-amber-400' : partner.status === 'Pending' ? 'text-amber-400' : 'text-slate-500'
                            }`}>
                              {partner.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <span className="text-white font-medium">{partner.name}</span>
                            {partner.is_walkin && (
                              <span className="ml-2 text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">
                                Walk-in
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-slate-300">{partner.email}</td>
                      <td className="p-4 text-slate-300">{partner.mobile}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={partner.status === 'Active'}
                            onCheckedChange={() => handleToggleStatus(partner.id, partner.status)}
                            className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-slate-600"
                          />
                          <span className={`text-sm font-medium ${
                            partner.status === 'Active' ? 'text-emerald-400' : partner.status === 'Pending' ? 'text-amber-400' : 'text-slate-500'
                          }`}>
                            {partner.status}
                          </span>
                        </div>
                      </td>
                      <td className="p-4 text-slate-400 text-sm">
                        {new Date(partner.created_at).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </td>
                      <td className="p-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-slate-400 hover:text-white hover:bg-slate-800"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
                            <DropdownMenuItem
                              onClick={() => openEditModal(partner)}
                              className="text-slate-300 hover:text-white hover:bg-slate-800 cursor-pointer"
                            >
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeletePartner(partner.id)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => openHardDelete(partner)}
                              className="text-red-500 hover:text-red-400 hover:bg-red-500/10 cursor-pointer"
                              data-testid={`dashboard-hard-delete-${partner.id}`}
                            >
                              <AlertTriangle className="w-4 h-4 mr-2" />
                              Delete Permanently
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Partner Modal */}
      <Dialog open={isModalOpen} onOpenChange={closeModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingPartner ? 'Edit Partner' : 'Add New Partner'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5 mt-4">
            <div>
              <Label htmlFor="name" className="text-slate-300 mb-2 block">
                Name
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleFormChange}
                placeholder="Enter partner name"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-slate-300 mb-2 block">
                Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleFormChange}
                placeholder="Enter email address"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
              />
            </div>
            <div>
              <Label htmlFor="mobile" className="text-slate-300 mb-2 block">
                Mobile
              </Label>
              <Input
                id="mobile"
                name="mobile"
                type="tel"
                required
                value={formData.mobile}
                onChange={handleFormChange}
                placeholder="Enter mobile number"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={closeModal}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
              >
                {isLoading ? (editingPartner ? 'Updating...' : 'Adding...') : (editingPartner ? 'Update Partner' : 'Add Partner')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Restore Deleted Partner Prompt */}
      <Dialog open={!!restorePrompt} onOpenChange={(open) => !open && setRestorePrompt(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md" data-testid="restore-partner-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Restore Deleted Partner?</DialogTitle>
          </DialogHeader>
          {restorePrompt && (
            <div className="space-y-4 mt-2">
              <p className="text-slate-300 text-sm">
                A deleted partner with the email{' '}
                <span className="text-amber-400 font-semibold">{restorePrompt.email}</span> exists.
              </p>
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Name</span>
                  <span className="text-white font-medium">{restorePrompt.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Deleted on</span>
                  <span className="text-white">
                    {restorePrompt.deleted_at ? new Date(restorePrompt.deleted_at).toLocaleDateString() : '—'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Orphaned clients</span>
                  <span className="text-white">{restorePrompt.client_count ?? 0}</span>
                </div>
              </div>
              <p className="text-slate-400 text-xs">
                Restoring will reactivate the partner and automatically re-link all{' '}
                {restorePrompt.client_count ?? 0} of their clients. Alternatively, cancel and use a different email.
              </p>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                  onClick={() => setRestorePrompt(null)}
                  data-testid="restore-partner-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRestoreDeleted}
                  disabled={isLoading}
                  className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                  data-testid="restore-partner-confirm"
                >
                  {isLoading ? 'Restoring...' : 'Restore Partner'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Hard-Delete Partner Dialog */}
      <HardDeletePartnerDialog
        target={hardDeleteTarget}
        confirmText={hardDeleteConfirmText}
        setConfirmText={setHardDeleteConfirmText}
        onClose={closeHardDelete}
        onConfirm={handleHardDelete}
        isDeleting={hardDeleting}
        result={hardDeleteResult}
      />
    </div>
  );
};

export default Dashboard;
